import { useState } from "react";
import { HomepageContainer } from "./HomepageContainer";
import { Skeleton } from "src/ccl/blocks/skeleton";
import { Icon, Img, Logo, Text } from "src/ccl/document";
import { Box, ContentContainer, Flex, Grid } from "src/ccl/layout";
import { ExternalLink, Link } from "src/ccl/navigation";
import { styled } from "src/ccl/stitches";
import { useStoreModel, useUserKindContext } from "src/hooks";
import { sendToAmplitude, trackEvent } from "src/utils/analytics";
import downloadAndroid from "src/pages/marketing/homepage/assets/download-android.svg";
import downloadIos from "src/pages/marketing/homepage/assets/download-ios.svg";

const Divider = styled("hr", {
  height: "1px",
  border: "none",
  backgroundColor: "$grey3",
});

const ListTitle = styled(Text, {
  paddingBottom: "10px",
  fontFamily: "$sans",
  fontSize: "$16",
  fontWeight: "$bold",
  lineHeight: "$26",
});

const StyledList = styled("ul", {
  listStyleType: "none",
  margin: 0,
  padding: 0,
});

const StyledListItem = styled("li", {
  paddingTop: "10px",
  paddingBottom: "10px",
});

const linkStyles = {
  textDecoration: "none",
  fontFamily: "$sansNew",
  fontSize: "$16",
  lineHeight: "$32",
  letterSpacing: "0.02em",
};

const FooterLink = styled(Link, linkStyles);
const ExternalFooterLink = styled(ExternalLink, linkStyles);

const FooterLayout = styled(Grid, {
  gridColumns: 1,
  gap: "30px",

  "@lg": {
    gridColumns: "364px minmax(0, 1fr)",
  },

  variants: {
    wide: {
      true: {
        "@xl": {
          gridColumns: "minmax(320px, 4fr) minmax(0, 8fr)",
          gap: "60px",
        },
      },
    },
  },
});

const FooterLinks = styled(Grid, {
  flex: 1,
  gridColumns: 2,
  gap: "20px",

  "@md": {
    gridColumns: 4,
  },

  variants: {
    wide: {
      true: {
        "@xl": {
          columnGap: "48px",
        },
      },
    },
  },
});

const FooterColumn = styled(Box, {
  width: "140px",

  variants: {
    wide: {
      true: {
        width: "auto",
      },
    },
  },
});

type FooterProps = {
  wide?: boolean;
};

export const Footer = ({ wide = false }: FooterProps) => {
  const [googleImgLoaded, setGoogleImgLoaded] = useState(false);
  const [appleImgLoaded, setAppleImgLoaded] = useState(false);
  const currentUser = useStoreModel("currentUser");
  const userKindContext = useUserKindContext(currentUser);
  const signedOut = userKindContext === "signedOut";
  const Container = wide ? HomepageContainer : ContentContainer;

  return (
    <footer>
      <Container data-test-id="MarketingFooterContainer">
        <Flex
          css={{
            flexDirection: "column",
            gap: "30px",
          }}
        >
          <FooterLayout wide={wide}>
            <Flex
              css={{
                flexDirection: "column",
                gap: "18px",
              }}
            >
              <Box>
                <Logo variant="full" />
              </Box>
              <Text
                css={{
                  fontFamily: "$sansNew",
                  fontSize: "$16",
                  fontWeight: "$medium",
                  lineHeight: "25px",
                  letterSpacing: "0.02em",
                }}
              >
                Download our app for creatives
              </Text>
              <Flex>
                <ExternalLink to="https://contact.xyz/apps/android">
                  <Img
                    loading="lazy"
                    onLoad={() => setGoogleImgLoaded(true)}
                    src={downloadAndroid}
                    alt="download-android-contact"
                  />
                  {!googleImgLoaded && <Skeleton height={53} width={176} />}
                </ExternalLink>
                <ExternalLink to="https://contact.xyz/apps/ios">
                  <Img
                    loading="lazy"
                    onLoad={() => setAppleImgLoaded(true)}
                    src={downloadIos}
                    alt="download-ios-contact"
                  />
                  {!appleImgLoaded && <Skeleton height={53} width={176} />}
                </ExternalLink>
              </Flex>
            </Flex>
            <FooterLinks wide={wide}>
              <FooterColumn wide={wide}>
                <ListTitle>Account</ListTitle>
                <StyledList>
                  {signedOut ? (
                    <StyledListItem>
                      <FooterLink to="/sign-in">Sign In</FooterLink>
                    </StyledListItem>
                  ) : (
                    <StyledListItem>
                      <FooterLink to="/dashboard">Dashboard</FooterLink>
                    </StyledListItem>
                  )}
                  {(signedOut || userKindContext === "client") && (
                    <StyledListItem>
                      <FooterLink
                        to="/jobs/new"
                        onClick={() => sendToAmplitude("footer - book talent")}
                      >
                        Create a job
                      </FooterLink>
                    </StyledListItem>
                  )}
                  <StyledListItem>
                    <FooterLink to="/agencies">Agency directory</FooterLink>
                  </StyledListItem>
                  {!signedOut && (
                    <StyledListItem>
                      <FooterLink
                        to="/"
                        onClick={(e) => {
                          e.preventDefault();
                          trackEvent("footer:signOut");
                          currentUser.logOut();
                        }}
                      >
                        Sign out
                      </FooterLink>
                    </StyledListItem>
                  )}
                </StyledList>
              </FooterColumn>
              <FooterColumn wide={wide}>
                <ListTitle>Contact for</ListTitle>
                <StyledList>
                  <StyledListItem>
                    <FooterLink
                      to="/for/clients"
                      onClick={() =>
                        sendToAmplitude("footer - contact for clients")
                      }
                    >
                      Clients
                    </FooterLink>
                  </StyledListItem>
                  <StyledListItem>
                    <FooterLink to="/for/agencies">Agencies</FooterLink>
                  </StyledListItem>
                  <StyledListItem>
                    <FooterLink to="/for/creatives">Creatives</FooterLink>
                  </StyledListItem>
                </StyledList>
              </FooterColumn>
              <FooterColumn wide={wide}>
                <ListTitle>Company</ListTitle>
                <StyledList>
                  <StyledListItem>
                    <FooterLink to="/about">About us</FooterLink>
                  </StyledListItem>
                  <StyledListItem>
                    <FooterLink to="/careers">Careers</FooterLink>
                  </StyledListItem>
                  <StyledListItem>
                    <FooterLink
                      to="/backstage"
                      onClick={() => sendToAmplitude("footer - backstage blog")}
                    >
                      Backstage
                    </FooterLink>
                  </StyledListItem>
                  <StyledListItem>
                    <ExternalFooterLink
                      to={"mailto:help@contact.xyz"}
                      eventName="footer:contact"
                    >
                      Get help
                    </ExternalFooterLink>
                  </StyledListItem>
                </StyledList>
              </FooterColumn>
              <FooterColumn wide={wide}>
                <ListTitle>Legal</ListTitle>
                <StyledList>
                  <StyledListItem>
                    <FooterLink to="/terms">Terms</FooterLink>
                  </StyledListItem>
                  <StyledListItem>
                    <FooterLink to="/privacy">Privacy Policy</FooterLink>
                  </StyledListItem>
                  <StyledListItem>
                    <FooterLink
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        trackEvent("footer:cookies");
                        if (window.cookiehub) {
                          window.cookiehub.openSettings();
                        }
                      }}
                    >
                      Cookies
                    </FooterLink>
                  </StyledListItem>
                </StyledList>
              </FooterColumn>
            </FooterLinks>
          </FooterLayout>
          <Flex
            css={{
              flexDirection: "column",
              gap: "30px",
              paddingBottom: "30px",
            }}
          >
            <Divider />
            <Flex
              css={{
                justifyContent: "space-between",
              }}
            >
              <Text
                css={{
                  fontFamily: "$sans",
                  fontSize: "$14",
                  lineHeight: "22px",
                }}
              >
                © 2026 Cultural Utilities Ltd
              </Text>
              <Flex
                css={{
                  gap: "10px",
                }}
              >
                <ExternalLink
                  to="https://www.instagram.com/contact.xyz"
                  aria-label="instagram"
                  eventName="footer:instagram"
                >
                  <Icon variant="instagram" size={28} />
                </ExternalLink>
                <ExternalLink
                  to="https://www.tiktok.com/@contact.creatives"
                  aria-label="tiktok"
                >
                  <Icon variant="tiktok" size={28} />
                </ExternalLink>
                <ExternalLink
                  to="https://www.linkedin.com/company/contact-creatives/"
                  aria-label="linkedIn"
                >
                  <Icon variant="linkedIn" size={28} />
                </ExternalLink>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Container>
    </footer>
  );
};
